import React, { useMemo } from 'react';
import type { SqlThemePreviewStyle } from '../utils/monacoThemes.ts';
import { SQL_AGGREGATE_FUNCTIONS } from '../utils/monacoSqlLanguage.ts';
import './css/SqlSyntaxPreview.css';

const SQL_KEYWORDS = new Set([
  'SELECT', 'FROM', 'WHERE', 'AND', 'OR', 'AS', 'JOIN', 'ON', 'IN', 'NULL',
  'BY', 'ORDER', 'GROUP', 'HAVING', 'INSERT', 'UPDATE', 'DELETE', 'INTO',
  'VALUES', 'SET', 'CREATE', 'TABLE', 'INDEX', 'DISTINCT', 'LIMIT', 'OFFSET',
  'IS', 'NOT', 'LIKE', 'BETWEEN', 'INNER', 'LEFT', 'RIGHT', 'OUTER', 'FULL',
  'COALESCE',
]);

/** Встроенные функции, включая агрегаты (predefined в Monaco). */
const SQL_PREDEFINED = new Set([
  ...SQL_AGGREGATE_FUNCTIONS,
  'ROUND', 'RANK', 'ROW_NUMBER', 'NULLIF', 'CAST', 'UPPER', 'LOWER',
]);

type TokenType = 'comment' | 'keyword' | 'predefined' | 'string' | 'number' | 'text';

type Token = {
  type: TokenType;
  value: string;
};

function tokenizeSqlLine(line: string): Token[] {
  const tokens: Token[] = [];
  let index = 0;

  while (index < line.length) {
    const rest = line.slice(index);

    if (rest.startsWith('--')) {
      tokens.push({ type: 'comment', value: rest });
      break;
    }

    if (line[index] === "'") {
      let end = index + 1;
      while (end < line.length && line[end] !== "'") {
        end += 1;
      }
      if (end < line.length) {
        end += 1;
      }
      tokens.push({ type: 'string', value: line.slice(index, end) });
      index = end;
      continue;
    }

    const wordMatch = rest.match(/^([a-zA-Z_][a-zA-Z0-9_]*)/);
    if (wordMatch) {
      const word = wordMatch[1];
      const upper = word.toUpperCase();
      let type: TokenType = 'text';
      if (SQL_PREDEFINED.has(upper)) {
        type = 'predefined';
      } else if (SQL_KEYWORDS.has(upper)) {
        type = 'keyword';
      }
      tokens.push({ type, value: word });
      index += word.length;
      continue;
    }

    const numberMatch = rest.match(/^\d+/);
    if (numberMatch) {
      tokens.push({ type: 'number', value: numberMatch[0] });
      index += numberMatch[0].length;
      continue;
    }

    tokens.push({ type: 'text', value: line[index] });
    index += 1;
  }

  return tokens;
}

function colorForToken(type: TokenType, style: SqlThemePreviewStyle): string {
  switch (type) {
    case 'keyword':
      return style.keyword;
    case 'predefined':
      return style.predefined ?? style.keyword;
    case 'string':
      return style.string;
    case 'comment':
      return style.comment;
    case 'number':
      return style.number;
    default:
      return style.foreground;
  }
}

export type SqlSyntaxPreviewProps = {
  code: string;
  style: SqlThemePreviewStyle;
};

export function SqlSyntaxPreview({ code, style }: SqlSyntaxPreviewProps) {
  const lines = useMemo(
    () =>
      code.split('\n').map((line) => {
        if (/^\s*--/.test(line)) {
          return [{ type: 'comment' as const, value: line }];
        }
        return tokenizeSqlLine(line);
      }),
    [code],
  );

  return (
    <pre
      className="sql-syntax-preview"
      style={{
        backgroundColor: style.background,
        color: style.foreground,
      }}
    >
      <code>
        {lines.map((tokens, lineIndex) => (
          <span key={lineIndex} className="sql-syntax-preview__line">
            {tokens.map((token, tokenIndex) => (
              <span
                key={tokenIndex}
                style={{
                  color: colorForToken(token.type, style),
                  fontStyle: token.type === 'comment' ? 'italic' : undefined,
                  fontWeight: token.type === 'keyword' ? 600 : undefined,
                }}
              >
                {token.value}
              </span>
            ))}
          </span>
        ))}
      </code>
    </pre>
  );
}
